import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { cn } from "@/components/podkit/lib/cn";
import { useIsVersionUpdateAvailable } from "@/hooks/use-nudges";
import { markNudgeShown } from "@/version-tracker";
import { useCallback, type FC } from "react";

/**
 * Small blue chip shown in the sidebar header (next to the collapse button)
 * when a version update is available. Clicking refreshes the page.
 */
export const UpdateChip: FC = () => {
    const visible = useIsVersionUpdateAvailable();
    const handleRefresh = useRefresh();

    if (!visible) {
        return null;
    }

    return (
        <button
            onClick={handleRefresh}
            className={cn(
                "flex h-6 items-center gap-1 rounded-full px-2 text-xs font-medium",
                "bg-surface-brand text-content-brand",
                "dark:bg-content-brand/25 dark:text-surface-brand/80",
                "hover:opacity-90",
                "transition-opacity",
            )}
            aria-label="Update available — click to refresh"
            data-tracking-id="sidebar-update-chip"
            data-testid="sidebar-update-chip"
        >
            <IconRefresh size="sm" className="size-3" />
            <span>Update</span>
        </button>
    );
};

/**
 * Small blue dot overlaid on the collapsed lettermark.
 * Non-interactive — clicking the lettermark area expands the sidebar,
 * revealing the UpdateChip where the user can trigger a refresh.
 */
export const UpdateDot: FC = () => {
    const visible = useIsVersionUpdateAvailable();

    if (!visible) {
        return null;
    }

    return (
        <span
            className="pointer-events-none absolute -right-1.5 -top-1.5 z-20 flex size-5 items-center justify-center"
            aria-hidden="true"
            data-testid="sidebar-update-dot"
        >
            <span
                className={cn(
                    "size-2.5 rounded-full",
                    "bg-content-brand dark:bg-surface-brand/80",
                )}
            />
        </span>
    );
};

function useRefresh() {
    return useCallback(() => {
        markNudgeShown();
        window.location.reload();
    }, []);
}
